import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Drawer,
  DrawerForm,
  DrawerFormActions,
  FormField,
  Select,
  Textarea,
} from '../../components/index.js';

const GUIDE_STORAGE_KEY = 'dha_prime__rapid_leadership_guide';
const GUIDE_EVENT = 'dha-prime-rapid-guide-updated';
const ACTIVITY_STORAGE_KEY_PREFIX = 'dha_prime__rapid_activity__';
const ACTIVITY_EVENT = 'dha-prime-rapid-activity-updated';

export const RAPID_GUIDE_STEPS = [
  {
    id: 'command-center',
    label: 'Open RAPID Command Center',
    hint: 'Start at the portfolio leadership view.',
  },
  {
    id: 'triage-prioritization',
    label: 'Review intake/triage and prioritization',
    hint: 'Confirm why requests become RAPIDs and how they are ranked.',
  },
  {
    id: 'open-flagship',
    label: 'Open IMR/PHA RAPID',
    hint: 'Move from portfolio view into the flagship workspace.',
  },
  {
    id: 'pod-risk-driver',
    label: 'Inspect Pod 3 risk and driver diagrams',
    hint: 'Review both the staffing-risk story and Diagnose artifact parity.',
  },
  {
    id: 'inspect-evidence',
    label: 'Inspect evidence for criteria, metric, and artifact',
    hint: 'Open at least one evidence drawer from each major trust surface.',
  },
  {
    id: 'lineage',
    label: 'Open lineage graph',
    hint: 'Follow how the RAPID backstory survives into downstream work.',
  },
  {
    id: 'brief',
    label: 'Generate governance brief',
    hint: 'Open the leadership-ready brief preview.',
  },
  {
    id: 'aar',
    label: 'Add AAR feedback and confirm follow-on action',
    hint: 'Turn a lesson into visible work.',
  },
  {
    id: 'return',
    label: 'Return for mitigation',
    hint: 'Exercise the recoverable governance path.',
  },
  {
    id: 'validate',
    label: 'Validate sponsor package',
    hint: 'Confirm sponsor acceptance after mitigation.',
  },
  {
    id: 'graduate',
    label: 'Graduate to project',
    hint: 'Create the durable follow-on project.',
  },
  {
    id: 'project-origin',
    label: 'Open project origin/backstory',
    hint: 'Verify the project is not orphaned from RAPID context.',
  },
  {
    id: 'ask-prime',
    label: 'Ask PRIME about the RAPID/project',
    hint: 'Use AI context to summarize what happened.',
  },
  {
    id: 'demo-feedback',
    label: 'Submit feedback',
    hint: 'Capture leadership evaluation reaction in the app.',
  },
  {
    id: 'reset',
    label: 'Reset to baseline',
    hint: 'Replay the experience from a clean baseline.',
  },
];

function readGuideState() {
  if (typeof window === 'undefined') {
    return { started: false, dismissed: false, completedStepIds: [] };
  }
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(GUIDE_STORAGE_KEY) || '{}');
    return {
      started: Boolean(parsed.started),
      dismissed: Boolean(parsed.dismissed),
      completedStepIds: Array.isArray(parsed.completedStepIds) ? parsed.completedStepIds : [],
    };
  } catch {
    return { started: false, dismissed: false, completedStepIds: [] };
  }
}

function writeGuideState(nextState) {
  if (typeof window === 'undefined') return nextState;
  window.sessionStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(nextState));
  window.dispatchEvent(new CustomEvent(GUIDE_EVENT, { detail: nextState }));
  return nextState;
}

function activityStorageKey(rapidId) {
  return `${ACTIVITY_STORAGE_KEY_PREFIX}${rapidId}`;
}

function readRapidActivityEntries(rapidId) {
  if (typeof window === 'undefined' || !rapidId) return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(activityStorageKey(rapidId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function emitRapidActivity(rapidId, entries) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACTIVITY_EVENT, { detail: { rapidId, entries } }));
}

function writeRapidActivityEntries(rapidId, entries) {
  if (typeof window === 'undefined' || !rapidId) return entries;
  window.sessionStorage.setItem(activityStorageKey(rapidId), JSON.stringify(entries));
  emitRapidActivity(rapidId, entries);
  return entries;
}

export function appendRapidActivityEntry(rapidId, entry) {
  if (!rapidId) return [];
  const nextEntry = {
    id: entry?.id || `rapid-activity-${Date.now()}`,
    title: entry?.title || 'RAPID activity updated',
    detail: entry?.detail || '',
    occurredAt: entry?.occurredAt || new Date().toISOString(),
  };
  const nextEntries = [nextEntry, ...readRapidActivityEntries(rapidId)];
  return writeRapidActivityEntries(rapidId, nextEntries);
}

export function clearRapidActivityState() {
  if (typeof window === 'undefined') return;
  const keysToDelete = [];
  for (let index = 0; index < window.sessionStorage.length; index += 1) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(ACTIVITY_STORAGE_KEY_PREFIX)) keysToDelete.push(key);
  }
  keysToDelete.forEach((key) => {
    window.sessionStorage.removeItem(key);
    emitRapidActivity(key.slice(ACTIVITY_STORAGE_KEY_PREFIX.length), []);
  });
}

export function resetRapidActivityEntries(rapidId) {
  if (!rapidId) return [];
  clearRapidActivityState();
  return writeRapidActivityEntries(rapidId, [
    {
      id: `rapid-activity-reset-${rapidId}`,
      title: 'Baseline restored',
      detail:
        'Guide progress and session changes were reset while leadership feedback was preserved.',
      occurredAt: new Date().toISOString(),
    },
  ]);
}

export function useRapidActivityFeed(rapidId) {
  const [entries, setEntries] = useState(() => readRapidActivityEntries(rapidId));

  useEffect(() => {
    setEntries(readRapidActivityEntries(rapidId));
  }, [rapidId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sync = (event) => {
      if (!event?.detail?.rapidId || event.detail.rapidId === rapidId) {
        setEntries(readRapidActivityEntries(rapidId));
      }
    };
    window.addEventListener(ACTIVITY_EVENT, sync);
    return () => window.removeEventListener(ACTIVITY_EVENT, sync);
  }, [rapidId]);

  return entries;
}

export function clearRapidGuideState() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(GUIDE_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(GUIDE_EVENT, { detail: readGuideState() }));
}

export function markRapidGuideStep(stepId) {
  const current = readGuideState();
  if (!current.started || current.completedStepIds.includes(stepId)) return current;
  return writeGuideState({
    ...current,
    completedStepIds: [...current.completedStepIds, stepId],
  });
}

export function useRapidGuideState() {
  const [state, setState] = useState(() => readGuideState());

  useEffect(() => {
    const sync = (event) => setState(event?.detail || readGuideState());
    window.addEventListener(GUIDE_EVENT, sync);
    return () => window.removeEventListener(GUIDE_EVENT, sync);
  }, []);

  return {
    state,
    startGuide() {
      setState(
        writeGuideState({
          started: true,
          dismissed: false,
          completedStepIds: readGuideState().completedStepIds,
        }),
      );
    },
    dismissGuide() {
      const current = readGuideState();
      setState(writeGuideState({ ...current, dismissed: true }));
    },
    reopenGuide() {
      const current = readGuideState();
      setState(writeGuideState({ ...current, started: true, dismissed: false }));
    },
    markStep(stepId) {
      setState(markRapidGuideStep(stepId));
    },
    clearGuide() {
      clearRapidGuideState();
      setState(readGuideState());
    },
  };
}

export function LeadershipDemoGuidePanel({ state, compact = false, onStart, onDismiss, onReopen }) {
  const completed = new Set(state?.completedStepIds || []);
  const completedCount = completed.size;
  const totalSteps = RAPID_GUIDE_STEPS.length;
  const progressPct = Math.round((completedCount / totalSteps) * 100);
  const nextStep = RAPID_GUIDE_STEPS.find((step) => !completed.has(step.id)) || null;

  if (compact) {
    return (
      <button
        type="button"
        className="rapid-guide-compact"
        data-testid="rapid-leadership-guide-reopen"
        onClick={onReopen}
      >
        <span className="eyebrow">Leadership guide</span>
        <strong>
          {completedCount}/{totalSteps} complete
        </strong>
      </button>
    );
  }

  return (
    <section className="rapid-guide-card" data-testid="rapid-leadership-guide">
      <div className="rapid-section-title">
        <div>
          <div className="eyebrow">Leadership evaluation</div>
          <h3>Explore RAPID in 12 minutes</h3>
        </div>
        {state?.started ? (
          <button type="button" className="btn ghost sm" onClick={onDismiss}>
            Hide guide
          </button>
        ) : null}
      </div>
      <p className="muted">
        Follow the flagship RAPID-to-PRIME story, inspect trust surfaces, and replay safely.
      </p>
      {!state?.started ? (
        <div className="rapid-action-row" style={{ marginTop: 12 }}>
          <button type="button" className="btn primary sm" onClick={onStart}>
            Start leadership evaluation
          </button>
        </div>
      ) : (
        <>
          <div className="rapid-guide-progress" aria-label={`${progressPct}% complete`}>
            <div className="rapid-guide-progress-bar" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="rapid-guide-summary">
            <strong>{progressPct}% complete</strong>
            <span className="muted">
              {nextStep ? `Next: ${nextStep.label}` : 'You completed the RAPID-to-PRIME lifecycle.'}
            </span>
          </div>
          <ol className="rapid-guide-list">
            {RAPID_GUIDE_STEPS.map((step) => {
              const done = completed.has(step.id);
              return (
                <li
                  key={step.id}
                  className={`rapid-guide-step${done ? ' done' : ''}`}
                  data-testid={`rapid-guide-step-${step.id}`}
                >
                  <span className="rapid-guide-step-index" aria-hidden>
                    {done ? '✓' : RAPID_GUIDE_STEPS.findIndex((item) => item.id === step.id) + 1}
                  </span>
                  <div>
                    <strong>{step.label}</strong>
                    <div className="muted small">{step.hint}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
}

export function RapidEvidenceDrawer({ open, onClose, evidence }) {
  return (
    <Drawer open={open} onClose={onClose} title="Evidence" width={520}>
      {evidence ? (
        <div className="rapid-evidence-drawer" data-testid="rapid-evidence-drawer">
          <div className="rapid-note-card">
            <div className="eyebrow">Evidence</div>
            <h3>{evidence.title}</h3>
            <p>{evidence.summary}</p>
          </div>
          <div className="rapid-note-card" style={{ marginTop: 14 }}>
            <div className="eyebrow">Source and trust</div>
            <ul className="rapid-list">
              <li>
                <strong>Source:</strong> {evidence.source || 'RAPID evidence'}
              </li>
              <li>
                <strong>Owner:</strong> {evidence.owner || 'RAPID team'}
              </li>
              <li>
                <strong>Confidence:</strong> {evidence.confidence || 'Confidence pending'}
              </li>
              <li>
                <strong>Status:</strong> {evidence.status || 'available'}
              </li>
              <li>
                <strong>Last updated:</strong> {evidence.lastUpdated || 'Baseline'}
              </li>
            </ul>
            <p className="muted small" style={{ marginTop: 12 }}>
              This value is sourced from RAPID evidence. No live PHI/CUI is used.
            </p>
          </div>
          {Array.isArray(evidence.relatedLinks) && evidence.relatedLinks.length ? (
            <div className="rapid-note-card" style={{ marginTop: 14 }}>
              <div className="eyebrow">Related links</div>
              <ul className="rapid-list">
                {evidence.relatedLinks.map((link) => (
                  <li key={`${link.href}-${link.label}`}>
                    <Link to={link.href}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="rapid-empty-inline">
          Select a RAPID evidence surface to inspect it here.
        </div>
      )}
    </Drawer>
  );
}

export function RapidLineageGraph({ nodes, onOpenEvidence }) {
  return (
    <div
      className="rapid-lineage-graph"
      data-testid="rapid-lineage-graph"
      aria-label="RAPID lineage graph from request to graduated project"
    >
      {nodes.map((node, index) => (
        <div key={node.id} className="rapid-lineage-segment">
          <button
            type="button"
            className="rapid-lineage-node"
            data-testid={node.testId || `lineage-node-${node.id}`}
            onClick={() => onOpenEvidence?.(node.evidence)}
          >
            <span className="eyebrow">{node.kind}</span>
            <strong>{node.label}</strong>
            <span className="muted small">{node.detail}</span>
          </button>
          {index < nodes.length - 1 ? (
            <div
              className="rapid-lineage-edge"
              data-testid={node.edgeTestId || undefined}
              aria-hidden
            >
              <span>→</span>
              <small>{nodes[index + 1].edgeLabel || 'Carried forward'}</small>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function RapidActivityFeedPanel({ entries = [] }) {
  return (
    <section className="rapid-note-card" data-testid="rapid-activity-feed">
      <div className="eyebrow">Session guide</div>
      <h3>Recent RAPID activity</h3>
      {entries.length ? (
        <ul className="rapid-list">
          {entries.map((entry) => (
            <li key={entry.id}>
              <strong>{entry.title}</strong>
              {entry.detail ? <div className="muted small">{entry.detail}</div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">
          Free-click actions will appear here so leadership evaluators can see what changed.
        </p>
      )}
    </section>
  );
}

export function RapidGraduationSuccessPanel({ open, onClose, project, rapid, lineageCount = 0 }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="PRIME-derived project preview from RAPID context"
      width={560}
    >
      <div className="rapid-note-card" data-testid="rapid-graduation-success">
        <div className="eyebrow">Graduation preview</div>
        <h3>{project?.name || project?.id || 'Graduated project'}</h3>
        <p>
          Preview. Decisions, artifacts, metrics, lineage, lessons learned, and the value model are
          shown as carried forward from {rapid?.id}.
        </p>
        <ul className="rapid-list">
          <li>
            <strong>Project:</strong> {project?.id || '—'}
          </li>
          <li>
            <strong>Artifacts:</strong> {(project?.carriedForwardArtifactIds || []).length}
          </li>
          <li>
            <strong>Metrics:</strong> {(project?.carriedForwardMetricIds || []).length}
          </li>
          <li>
            <strong>Decisions:</strong> {(project?.carriedForwardDecisionIds || []).length}
          </li>
          <li>
            <strong>Lineage edges:</strong> {lineageCount}
          </li>
        </ul>
        <div className="rapid-action-row" style={{ marginTop: 14 }}>
          {project?.id ? (
            <Link className="btn primary sm" to={`/projects/${project.id}`}>
              Open PRIME Project
            </Link>
          ) : null}
          {rapid?.id ? (
            <Link className="btn ghost sm" to={`/rapids/${rapid.id}`}>
              Back to RAPID
            </Link>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}

export function DemoFeedbackDrawer({
  open,
  onClose,
  onSubmit,
  busy,
  draft,
  setDraft,
  feedbackItems = [],
}) {
  const reviewToolsEnabled = import.meta.env.VITE_ENABLE_REVIEW_TOOLS === 'true';
  const updateField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  if (!reviewToolsEnabled) return null;

  return (
    <Drawer open={open} onClose={onClose} title="Send feedback on this RAPID" width={580}>
      <DrawerForm
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.();
        }}
      >
        <div className="rapid-note-card" data-testid="rapid-demo-feedback-drawer">
          <div className="eyebrow">Leadership evaluation</div>
          <h3>What stood out and what is still needed?</h3>
          <p className="muted">
            Capture what impressed you, what is still missing for production, and what decision this
            evaluation supports.
          </p>
        </div>
        <div style={{ marginTop: 14 }}>
          <FormField label="What impressed you?" htmlFor="rapid-feedback-impressed" required>
            <Textarea
              id="rapid-feedback-impressed"
              value={draft.impressedBy}
              onChange={(value) => updateField('impressedBy', value)}
              rows={3}
            />
          </FormField>
        </div>
        <div style={{ marginTop: 14 }}>
          <FormField
            label="What is missing for production?"
            htmlFor="rapid-feedback-missing"
            required
          >
            <Textarea
              id="rapid-feedback-missing"
              value={draft.missingForProduction}
              onChange={(value) => updateField('missingForProduction', value)}
              rows={3}
            />
          </FormField>
        </div>
        <div style={{ marginTop: 14 }}>
          <FormField
            label="What decision does this help?"
            htmlFor="rapid-feedback-decision"
            required
          >
            <Textarea
              id="rapid-feedback-decision"
              value={draft.decisionSupported}
              onChange={(value) => updateField('decisionSupported', value)}
              rows={2}
            />
          </FormField>
        </div>
        <div style={{ marginTop: 14 }}>
          <FormField
            label="Does this replace current slide/email workflows?"
            htmlFor="rapid-feedback-workflow"
            required
          >
            <Select
              id="rapid-feedback-workflow"
              value={draft.workflowReplacement}
              onChange={(value) => updateField('workflowReplacement', value)}
              options={[
                { value: '', label: 'Select an answer' },
                { value: 'yes', label: 'Yes' },
                { value: 'partial', label: 'Partially' },
                { value: 'no', label: 'No' },
              ]}
            />
          </FormField>
        </div>
        <div style={{ marginTop: 14 }}>
          <FormField label="Optional contact / context" htmlFor="rapid-feedback-contact">
            <Textarea
              id="rapid-feedback-contact"
              value={draft.contactContext}
              onChange={(value) => updateField('contactContext', value)}
              rows={2}
            />
          </FormField>
        </div>
        {feedbackItems.length ? (
          <div className="rapid-note-card" style={{ marginTop: 14 }}>
            <div className="eyebrow">Captured in this evaluation</div>
            <ul className="rapid-list">
              {feedbackItems.map((item) => (
                <li key={item.id}>
                  <strong>{item.authorDisplayName || item.authorUserId || 'Evaluator'}</strong> —{' '}
                  {item.decisionSupported}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <DrawerFormActions
          onCancel={onClose}
          submitLabel={busy ? 'Submitting…' : 'Submit feedback'}
        />
      </DrawerForm>
    </Drawer>
  );
}

export function ProductionBuildoutPreviewDrawer({ open, onClose }) {
  return (
    <Drawer open={open} onClose={onClose} title="Production Buildout Preview" width={560}>
      <div className="rapid-note-card" data-testid="rapid-production-preview-drawer">
        <div className="eyebrow">Roadmap capability</div>
        <h3>Preview</h3>
        <p>
          This preview shows the production buildout direction. These are planned integration areas
          shown for context.
        </p>
        <ul className="rapid-list">
          <li>Identity integration and enterprise role workspaces</li>
          <li>Real data connectors and document search</li>
          <li>Audit controls, AI guardrails, and policy-aware traceability</li>
          <li>Enterprise reporting, telemetry, and workflow-scale operations</li>
        </ul>
      </div>
    </Drawer>
  );
}

export function DemoResetDrawer({ open, onClose, onConfirm, resetting }) {
  return (
    <Drawer open={open} onClose={onClose} title="Reset to Baseline" width={520}>
      <div className="rapid-note-card" data-testid="rapid-reset-demo-drawer">
        <div className="eyebrow">Replay the evaluation</div>
        <h3>{resetting ? 'Restoring RAPID baseline…' : 'Reset RAPID to baseline?'}</h3>
        <p>This restores the baseline so the evaluation path can be replayed from the beginning.</p>
        <ul className="rapid-list">
          <li>Guide progress will be cleared.</li>
          <li>RAPID changes will be cleared.</li>
          <li>Leadership feedback will be preserved while the baseline is restored.</li>
        </ul>
      </div>
      <DrawerForm onSubmit={onConfirm}>
        <DrawerFormActions
          onCancel={onClose}
          submitLabel={resetting ? 'Resetting…' : 'Reset baseline'}
        />
      </DrawerForm>
    </Drawer>
  );
}
